import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { client } from "@/data/client";
import { formatBRL, formatDelta, formatPct, cn } from "@/lib/utils";
import type { Period, PeriodPreset, MetricTab } from "@/data/types";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { SellerPerformance } from "./dashboard-sections/SellerPerformance";
import { SdrPerformance } from "./dashboard-sections/SdrPerformance";
import { ProductPerformance } from "./dashboard-sections/ProductPerformance";
import { DayDetailModal } from "./dashboard-sections/DayDetailModal";
import { Top3PerformersModal } from "./dashboard-sections/Top3PerformersModal";
import { DateRangePicker } from "@/components/DateRangePicker";
import { ThemeToggle } from "@/components/ThemeToggle";
import { ActivitiesTab } from "./tabs/ActivitiesTab";
import { ConversationsTab } from "./tabs/ConversationsTab";
import { CallsTab } from "./tabs/CallsTab";
import { SdrFunnelTab } from "./tabs/SdrFunnelTab";
import { CloserFunnelTab } from "./tabs/CloserFunnelTab";

const TAB_LABEL: Record<MetricTab, string> = {
  vendas: "Vendas",
  atividades: "Atividades",
  conversas: "Conversas",
  ligacoes: "Ligações",
  sdr: "SDR",
  closer: "Closer",
};

const TAB_ORDER: MetricTab[] = ["vendas", "atividades", "conversas", "ligacoes", "sdr", "closer"];

const TAB_SUBTITLE: Record<MetricTab, string> = {
  vendas: "Visão consolidada do desempenho comercial · atualização automática",
  atividades: "Tarefas programadas, execução e SLA do time comercial",
  conversas: "Volume de tickets, tempo de resposta e taxa de fechamento",
  ligacoes: "Telefonia integrada · tentativas, atendimentos e conexões efetivas",
  sdr: "Funil de prospecção · leads → conexões → agendadas → realizadas",
  closer: "Funil de fechamento · agendadas → realizadas → vendas → valor gerado",
};

type ChartMode = "bar" | "line";

const PRESET_LABEL: Record<PeriodPreset, string> = {
  today: "Hoje",
  yesterday: "Ontem",
  "7d": "Últimos 7 dias",
  "14d": "Últimos 14 dias",
  "30d": "Últimos 30 dias",
  "last-month": "Mês passado",
  "60d": "Últimos 60 dias",
  "90d": "Últimos 90 dias",
  "last-semester": "Último semestre",
  "last-year": "Ano passado",
  custom: "Data customizada",
};

const PRESET_ORDER: PeriodPreset[] = [
  "today",
  "yesterday",
  "7d",
  "14d",
  "30d",
  "last-month",
  "60d",
  "90d",
  "last-semester",
  "last-year",
  "custom",
];

const DEFAULT_PRESET: PeriodPreset = "30d";

const TODAY = new Date("2026-04-17");

function buildPeriod(
  preset: PeriodPreset,
  customRange?: { start: string; end: string },
  today = TODAY
): Period {
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const d = (offset: number) => {
    const x = new Date(today);
    x.setDate(x.getDate() + offset);
    return x;
  };
  const addMonths = (base: Date, months: number) => {
    const x = new Date(base);
    x.setMonth(x.getMonth() + months);
    return x;
  };

  switch (preset) {
    case "today":
      return { preset, start: iso(today), end: iso(today) };
    case "yesterday": {
      const y = iso(d(-1));
      return { preset, start: y, end: y };
    }
    case "7d":
      return { preset, start: iso(d(-6)), end: iso(today) };
    case "14d":
      return { preset, start: iso(d(-13)), end: iso(today) };
    case "30d":
      return { preset, start: iso(d(-29)), end: iso(today) };
    case "last-month": {
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last = new Date(today.getFullYear(), today.getMonth(), 0);
      return { preset, start: iso(first), end: iso(last) };
    }
    case "60d":
      return { preset, start: iso(d(-59)), end: iso(today) };
    case "90d":
      return { preset, start: iso(d(-89)), end: iso(today) };
    case "last-semester":
      return { preset, start: iso(addMonths(today, -6)), end: iso(today) };
    case "last-year": {
      const y = today.getFullYear() - 1;
      return { preset, start: `${y}-01-01`, end: `${y}-12-31` };
    }
    case "custom":
      return {
        preset,
        start: customRange?.start ?? iso(d(-16)),
        end: customRange?.end ?? iso(today),
      };
  }
}

function formatPeriodLabel(p: Period): string {
  const fmt = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
  };
  if (p.start === p.end) return fmt(p.start);
  return `${fmt(p.start)} – ${fmt(p.end)}`;
}

export function DashboardExecutivo({
  onNavigate,
  onPeriodChange,
}: {
  onNavigate?: (route: string) => void;
  onPeriodChange?: (p: { periodStart: string; periodEnd: string }) => void;
}) {
  const [preset, setPreset] = useState<PeriodPreset>(DEFAULT_PRESET);
  const [customRange, setCustomRange] = useState<{ start: string; end: string }>(() => {
    const iso = (d: Date) => d.toISOString().slice(0, 10);
    const d = new Date(TODAY);
    d.setDate(d.getDate() - 16);
    return { start: iso(d), end: iso(TODAY) };
  });
  const [chartMode, setChartMode] = useState<ChartMode>("bar");
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [topPerformersOpen, setTopPerformersOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<MetricTab>("vendas");

  const period = useMemo(() => buildPeriod(preset, customRange), [preset, customRange]);
  const periodArg = { periodStart: period.start, periodEnd: period.end };
  const filtersModified = preset !== DEFAULT_PRESET;

  // Propaga o período selecionado para o App pra que drill-downs full-page
  // (ex. AtividadesPorStatus) herdem o mesmo filtro.
  useEffect(() => {
    onPeriodChange?.(periodArg);
    // periodArg é estável por referência enquanto start/end não mudam
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period.start, period.end]);

  const handlePresetSelect = (p: PeriodPreset) => {
    setPreset(p);
    if (p !== "custom") setFilterOpen(false);
  };

  const handleCustomRangeApply = (r: { start: string; end: string }) => {
    setCustomRange(r);
    setPreset("custom");
  };

  const handleResetFilters = () => {
    setPreset(DEFAULT_PRESET);
    setFilterOpen(false);
  };

  const handleChartPointClick = (payload: unknown) => {
    if (payload && typeof payload === "object" && "date" in payload) {
      setSelectedDay((payload as { date: string }).date);
    }
  };

  const kpis = useQuery({ queryKey: ["kpis", periodArg], queryFn: () => client.getDashboardKpis(periodArg) });
  const daily = useQuery({ queryKey: ["daily", periodArg], queryFn: () => client.getDailyRevenue(periodArg) });
  const tags = useQuery({ queryKey: ["tags", periodArg], queryFn: () => client.getTopTags({ limit: 5, ...periodArg }) });
  const insights = useQuery({ queryKey: ["insights"], queryFn: () => client.getInsights() });

  return (
    <div className="min-h-screen bg-bg-primary px-12 py-10 space-y-8">
      {/* Top bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="h-2.5 w-2.5 rounded-full bg-brand-amber" />
          <span className="text-sm font-bold tracking-[0.2em] text-text-primary">HORIZON</span>
          <span className="text-sm text-text-muted">·</span>
          <span className="text-sm font-medium text-text-secondary">Clínica de Radiologia Odontológica</span>
        </div>

        <div className="flex items-center gap-3">
          <ThemeToggle />

          {filtersModified && (
            <button
              type="button"
              onClick={handleResetFilters}
              className="flex items-center gap-1.5 text-xs font-medium text-text-muted transition hover:text-text-primary"
            >
              <span>⟲</span>
              <span>Restaurar filtros</span>
            </button>
          )}

          <div className="relative">
            <button
              type="button"
              onClick={() => setFilterOpen((v) => !v)}
              className={cn(
                "flex items-center gap-2 rounded-full border px-3.5 py-2 transition",
                filterOpen
                  ? "border-brand-amber bg-bg-card"
                  : "border-border-subtle bg-bg-card hover:border-border-strong"
              )}
            >
              <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-text-muted">Período</span>
              <span className="text-sm font-medium text-text-primary">{formatPeriodLabel(period)}</span>
              <span className="text-xs text-text-muted">·</span>
              <span className="text-xs text-text-muted">{PRESET_LABEL[preset]}</span>
              <span className={cn("text-xs text-text-muted transition", filterOpen && "rotate-180")}>▾</span>
            </button>

            {filterOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setFilterOpen(false)} />
                <div className="absolute right-0 top-full z-20 mt-2">
                  <DateRangePicker
                    preset={preset}
                    displayRange={{ start: period.start, end: period.end }}
                    presetLabels={PRESET_LABEL}
                    presetOrder={PRESET_ORDER}
                    today={TODAY}
                    onPresetSelect={handlePresetSelect}
                    onRangeApply={handleCustomRangeApply}
                    onClose={() => setFilterOpen(false)}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Title */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight text-text-primary">Dashboard Executivo</h1>
        <p className="text-[15px] text-text-secondary">{TAB_SUBTITLE[activeTab]}</p>
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1 overflow-x-auto border-b border-border-subtle pb-px">
        {TAB_ORDER.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={cn(
              "relative px-4 py-3 text-sm font-medium transition",
              activeTab === tab
                ? "text-text-primary"
                : "text-text-muted hover:text-text-secondary"
            )}
          >
            {TAB_LABEL[tab]}
            {activeTab === tab && (
              <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-brand-amber" />
            )}
          </button>
        ))}
      </div>

      {activeTab === "atividades" && (
        <ActivitiesTab
          period={periodArg}
          onOpenStatus={(status) => onNavigate?.(`atividades-${status}`)}
        />
      )}
      {activeTab === "conversas" && <ConversationsTab period={periodArg} />}
      {activeTab === "ligacoes" && <CallsTab period={periodArg} />}
      {activeTab === "sdr" && <SdrFunnelTab period={periodArg} />}
      {activeTab === "closer" && <CloserFunnelTab period={periodArg} />}

      {activeTab === "vendas" && (<>

      {/* KPI row */}
      <div className="grid grid-cols-4 gap-5">
        <KpiCard
          label="Vendas Realizadas"
          value={kpis.data ? String(kpis.data.vendas) : "—"}
          delta={kpis.data ? formatDelta(kpis.data.vendasDeltaPct) : ""}
          deltaDir={kpis.data && kpis.data.vendasDeltaPct > 0 ? "up" : "down"}
        />
        <KpiCard
          label="Taxa de Conversão"
          value={kpis.data ? formatPct(kpis.data.conversao) : "—"}
          delta={kpis.data ? formatDelta(kpis.data.conversaoDeltaPp, "pp") : ""}
          deltaDir={kpis.data && kpis.data.conversaoDeltaPp > 0 ? "up" : "down"}
        />
        <KpiCard
          label="Ticket Médio"
          value={kpis.data ? formatBRL(kpis.data.ticketMedio) : "—"}
          delta={kpis.data ? formatDelta(kpis.data.ticketMedioDeltaPct) : ""}
          deltaDir={kpis.data && kpis.data.ticketMedioDeltaPct > 0 ? "up" : "down"}
        />
        <KpiCard
          label="Valor Total de Vendas"
          value={kpis.data ? formatBRL(kpis.data.valorTotal) : "—"}
          delta={kpis.data ? formatDelta(kpis.data.valorTotalDeltaPct) : ""}
          deltaDir={kpis.data && kpis.data.valorTotalDeltaPct > 0 ? "up" : "down"}
        />
      </div>

      {/* Main row: chart + top tags */}
      <div className="grid grid-cols-3 gap-5">
        <section className="col-span-2 rounded-lg border border-border-subtle bg-bg-card p-6 space-y-6">
          <div className="flex items-start justify-between gap-6">
            <div className="space-y-1">
              <h2 className="text-lg font-semibold tracking-tight text-text-primary">Evolução de Vendas</h2>
              <p className="text-[13px] text-text-muted">Valor transacionado por dia · {formatPeriodLabel(period)}</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3 text-xs text-text-secondary">
                <LegendDot color="bg-brand-amber" /> Abril 2026
                <LegendDot color="bg-text-muted" /> Março 2026 (ref.)
              </div>
              <ChartToggle mode={chartMode} onChange={setChartMode} />
            </div>
          </div>
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              {chartMode === "bar" ? (
                <BarChart data={daily.data ?? []} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="hsl(var(--border-subtle))" vertical={false} strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(v) => new Date(v).getDate().toString()}
                    stroke="hsl(var(--text-muted))"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke="hsl(var(--text-muted))"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => (v >= 1000 ? `R$${(v / 1000).toFixed(1)}k` : `R$${v}`)}
                    width={55}
                  />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: "hsl(var(--bg-card-hover))" }} />
                  <Bar
                    dataKey="amount"
                    fill="hsl(var(--brand-amber))"
                    radius={[4, 4, 0, 0]}
                    isAnimationActive={false}
                    onClick={(d) => handleChartPointClick(d?.payload ?? d)}
                    style={{ cursor: "pointer" }}
                  />
                </BarChart>
              ) : (
                <LineChart data={daily.data ?? []} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="hsl(var(--border-subtle))" vertical={false} strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(v) => new Date(v).getDate().toString()}
                    stroke="hsl(var(--text-muted))"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke="hsl(var(--text-muted))"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => (v >= 1000 ? `R$${(v / 1000).toFixed(1)}k` : `R$${v}`)}
                    width={55}
                  />
                  <Tooltip content={<ChartTooltip />} cursor={{ stroke: "hsl(var(--border-strong))" }} />
                  <Line
                    type="monotone"
                    dataKey="amountReference"
                    stroke="hsl(var(--text-muted))"
                    strokeWidth={1.5}
                    strokeDasharray="4 4"
                    dot={false}
                    isAnimationActive={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="amount"
                    stroke="hsl(var(--brand-amber))"
                    strokeWidth={2.5}
                    dot={{ r: 3, fill: "hsl(var(--brand-amber))", strokeWidth: 0 }}
                    activeDot={{
                      r: 6,
                      style: { cursor: "pointer" },
                      onClick: (_e: unknown, payload: unknown) => handleChartPointClick((payload as { payload?: unknown })?.payload ?? payload),
                    }}
                    isAnimationActive={false}
                  />
                </LineChart>
              )}
            </ResponsiveContainer>
          </div>
        </section>

        <section className="rounded-lg border border-border-subtle bg-bg-card p-6 space-y-6">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold tracking-tight text-text-primary">Top Tags em Conversão</h2>
            <p className="text-[13px] text-text-muted">Ordenado por taxa de fechamento</p>
          </div>
          <ul className="space-y-3.5">
            {(tags.data ?? []).map((t) => (
              <li key={t.tagId} className="flex items-center gap-3 text-sm">
                <span className="w-4 text-right font-bold text-text-muted">{t.rank}</span>
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: t.color }} />
                <span className="flex-1 font-medium text-text-primary">{t.tagName}</span>
                <span className="font-semibold text-brand-amber">{t.conversionRate}%</span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      {/* Insights row */}
      <div className="grid grid-cols-3 gap-5">
        {(insights.data ?? []).map((ins) => {
          const handle =
            ins.linkTo === "top-performer"
              ? () => setTopPerformersOpen(true)
              : ins.linkTo && onNavigate
                ? () => onNavigate(ins.linkTo!)
                : undefined;
          return <InsightCard key={ins.kind} {...ins} onClick={handle} />;
        })}
      </div>

      {/* Desempenho dos Vendedores */}
      <SellerPerformance period={periodArg} />

      {/* Desempenho dos SDRs */}
      <SdrPerformance period={periodArg} />

      {/* Produtos Vendidos */}
      <ProductPerformance period={periodArg} />

      </>)}

      <footer className="pt-8 text-center text-xs text-text-muted">
        Horizon Consultoria · CRM artesanal · demo preview · clique em qualquer dia do gráfico para ver o detalhamento
      </footer>

      {/* Modal drill-down do dia */}
      <DayDetailModal date={selectedDay} onClose={() => setSelectedDay(null)} />

      {/* Modal podium dos Top 3 vendedores */}
      <Top3PerformersModal
        open={topPerformersOpen}
        period={periodArg}
        onClose={() => setTopPerformersOpen(false)}
      />
    </div>
  );
}

function ChartToggle({ mode, onChange }: { mode: ChartMode; onChange: (m: ChartMode) => void }) {
  return (
    <div className="flex items-center rounded-md border border-border-subtle bg-bg-primary p-0.5">
      {(["bar", "line"] as const).map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => onChange(m)}
          className={cn(
            "px-3 py-1 text-xs font-medium transition",
            mode === m
              ? "rounded bg-bg-card text-text-primary shadow-sm"
              : "text-text-muted hover:text-text-secondary"
          )}
        >
          {m === "bar" ? "Barras" : "Linha"}
        </button>
      ))}
    </div>
  );
}

type TooltipPayload = {
  name: string;
  value: number;
  dataKey: string;
  color: string;
  payload: { date: string; amount: number; amountReference?: number };
};

function ChartTooltip({ active, payload, label }: { active?: boolean; payload?: TooltipPayload[]; label?: string }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const dateLabel = new Date(d.date ?? label ?? "").toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  return (
    <div className="rounded-md border border-border-strong bg-bg-card px-3 py-2 shadow-lg">
      <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-text-muted">{dateLabel}</div>
      <div className="flex items-center gap-2 text-sm">
        <span className="h-2 w-2 rounded-full bg-brand-amber" />
        <span className="text-text-secondary">Abril</span>
        <span className="ml-auto font-semibold text-text-primary">{formatBRL(d.amount)}</span>
      </div>
      {typeof d.amountReference === "number" && (
        <div className="flex items-center gap-2 text-sm">
          <span className="h-2 w-2 rounded-full bg-text-muted" />
          <span className="text-text-muted">Março (ref.)</span>
          <span className="ml-auto font-medium text-text-secondary">{formatBRL(d.amountReference)}</span>
        </div>
      )}
    </div>
  );
}

function KpiCard({
  label,
  value,
  delta,
  deltaDir,
}: {
  label: string;
  value: string;
  delta: string;
  deltaDir: "up" | "down";
}) {
  const chipColor = deltaDir === "up" ? "bg-success/15 text-success" : "bg-danger/15 text-danger";
  const arrow = deltaDir === "up" ? "▲" : "▼";
  return (
    <div className="rounded-lg border border-border-subtle bg-bg-card p-6 space-y-5">
      <span className="text-[11px] font-medium uppercase tracking-[0.1em] text-text-muted">{label}</span>
      <div className="text-4xl font-bold tracking-tight">{value}</div>
      <div className="flex items-center gap-2">
        <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold", chipColor)}>
          <span>{arrow}</span>
          <span>{delta}</span>
        </span>
        <span className="text-xs text-text-muted">vs mar/2026</span>
      </div>
    </div>
  );
}

function LegendDot({ color }: { color: string }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className={cn("h-2 w-2 rounded-full", color)} />
    </span>
  );
}

function InsightCard({
  heading,
  value,
  body,
  accentColor,
  onClick,
}: {
  heading: string;
  value: string;
  body: string;
  accentColor: "warning" | "success" | "info" | "danger";
  onClick?: () => void;
}) {
  const bar = {
    warning: "bg-warning",
    success: "bg-success",
    info: "bg-chart-2",
    danger: "bg-danger",
  }[accentColor];
  const interactive = !!onClick;
  return (
    <div
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={interactive ? (e) => (e.key === "Enter" || e.key === " ") && onClick!() : undefined}
      className={cn(
        "rounded-lg border border-border-subtle bg-bg-card p-5 space-y-3 transition",
        interactive && "cursor-pointer hover:border-border-strong hover:bg-bg-card-hover"
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className={cn("h-4 w-[3px] rounded", bar)} />
          <span className="text-[11px] font-medium uppercase tracking-[0.1em] text-text-muted">{heading}</span>
        </div>
        {interactive && <span className="text-xs text-text-muted">Ver detalhes →</span>}
      </div>
      <div className="text-[28px] font-bold leading-8 tracking-tight">{value}</div>
      <p className="text-[13px] leading-[18px] text-text-secondary">{body}</p>
    </div>
  );
}
